import { Box, Checkbox, HStack, Text } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { Calendar, View } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../../courts-calendar/courts-calendar.css';
import { translate } from '../../../i18n';
import { CoachingSessionStatus } from '../../../models/coach/dto/coaching-session.dto';
import { GameStatus } from '../../../models/game/dto/game.dto';
import { calendarEventPropGetter } from '../../courts-calendar/CourtsCalendarPage';
import CalendarCheckboxShowAllDay from './CalendarCheckboxShowAllDay';
import CalendarLegend from './CalendarLegend';
import CalendarNavigation from './CalendarNavigation';
import { calendarLocalizer } from './SharedCalendar';


type Props = {
    backgroundEvents: { start: Date; end: Date; title: string; }[];
    events: { start: Date; end: Date; title: string; }[]; onRangeChange: (start: Date, end: Date) => void;
    onTimeSlotSelected: (start: Date, end: Date) => void;
    onEventSelected: (event: any) => void;
}

const MonthlyCourtCalendar = ({ onTimeSlotSelected, onEventSelected, events, backgroundEvents, onRangeChange }: Props) => {
    const [calendarViewType, setCalendarViewType] = useState<View>('week');
    const [showAllHours, setShowAllHours] = useState(false);
    const [mergeAvailabilities, setMergeAvailabilities] = useState(false);
    const [showCanceledEvents, setShowCanceledEvents] = useState(false);
    const [currentCalendarDate, setCurrentCalendarDate] = useState<Date>(new Date());

    // Filter events based on canceled status
    const processedEvents = useMemo(() => {
        if (showCanceledEvents) return events;

        return events.filter(event => {
            const game = (event as any).game;
            const coachingSession = (event as any).coachingSession;

            // Filter out canceled games
            if (game && game.status === GameStatus.CANCELED) return false;

            // Filter out canceled coaching sessions
            if (coachingSession && coachingSession.status === CoachingSessionStatus.CANCELED) return false;

            return true;
        });
    }, [events, showCanceledEvents]);

    const processedBackgroundEvents = useMemo(() => {
        if (!mergeAvailabilities) return backgroundEvents;

        // Group availabilities by court title
        const groupedByCourt = backgroundEvents.reduce((acc, curr) => {
            if (!acc[curr.title]) {
                acc[curr.title] = [];
            }
            acc[curr.title].push(curr);
            return acc;
        }, {} as Record<string, typeof backgroundEvents>);

        // Merge availabilities for each court
        return Object.values(groupedByCourt).flatMap(courtEvents => {
            // Sort events by start time
            const sortedEvents = [...courtEvents].sort((a, b) => a.start.getTime() - b.start.getTime());

            const mergedEvents: typeof backgroundEvents = [];
            let currentEvent = sortedEvents[0];

            for (let i = 1; i < sortedEvents.length; i++) {
                const nextEvent = sortedEvents[i];

                // If events overlap or are adjacent, merge them
                if (currentEvent.end >= nextEvent.start) {
                    currentEvent = {
                        ...currentEvent,
                        end: new Date(Math.max(currentEvent.end.getTime(), nextEvent.end.getTime()))
                    };
                } else {
                    // No overlap, add current event and move to next
                    mergedEvents.push(currentEvent);
                    currentEvent = nextEvent;
                }
            }

            // Add the last event
            mergedEvents.push(currentEvent);

            return mergedEvents;
        });
    }, [backgroundEvents, mergeAvailabilities]);

    const minDate = useMemo(() => {
        if (showAllHours) return new Date(1970, 4, 1, 0, 0, 0, 0);
        if (calendarViewType === 'month') return null;
        if (!processedEvents.length && !processedBackgroundEvents.length) return null;
        const minHour = Math.min(...processedEvents.map(e => e.start.getHours()), ...processedBackgroundEvents.map(e => e.start.getHours()));
        return new Date(1970, 4, 1, minHour, 0, 0, 0);
    }, [processedEvents, processedBackgroundEvents, calendarViewType, showAllHours]);

    const maxDate = useMemo(() => {
        if (showAllHours) return new Date(1970, 4, 1, 23, 59, 59, 0);
        if (calendarViewType === 'month') return null;
        if (!processedEvents.length && !processedBackgroundEvents.length) return null;
        const maxHour = Math.max(...processedEvents.map(e => e.end.getHours()), ...processedBackgroundEvents.map(e => e.end.getHours()));
        return new Date(1970, 4, 1, Math.min(maxHour + 1, 23), 59, 59, 0);
    }, [processedEvents, processedBackgroundEvents, calendarViewType, showAllHours]);

    return (
        <Box px={{ base: 4, md: 12 }} pb={10}>
            {/* Calendar Navigation */}
            <CalendarNavigation
                onViewChange={(view) => setCalendarViewType(view)}
                onDateBoundsChange={(start, end) => onRangeChange(start, end)}
                onCurrentDateChange={(date) => setCurrentCalendarDate(date)}
                initialView={(calendarViewType === 'day' || calendarViewType === 'week') ? calendarViewType : 'week'}
            />

            {(calendarViewType === 'day' || calendarViewType === 'week') && (
                <Box mb={3}>
                    <HStack spacing={3} mb={2} fontSize="sm">
                        <CalendarCheckboxShowAllDay
                            selected={showAllHours}
                            setSelected={setShowAllHours}
                        />
                        <Checkbox
                            size="sm"
                            isChecked={mergeAvailabilities}
                            onChange={(e) => setMergeAvailabilities(e.target.checked)}
                        >
                            <Text fontSize="sm">{translate('calendar.merge_availabilities')}</Text>
                        </Checkbox>
                        <Checkbox
                            size="sm"
                            isChecked={showCanceledEvents}
                            onChange={(e) => setShowCanceledEvents(e.target.checked)}
                        >
                            <Text fontSize="sm">{translate('calendar.show_canceled_events')}</Text>
                        </Checkbox>
                    </HStack>
                    <CalendarLegend />
                </Box>
            )}

            <Box className="courts-calendar" h="calc(100vh - 350px)" minH="500px">
                <Calendar
                    culture={'fr'}
                    min={minDate}
                    max={maxDate}
                    localizer={calendarLocalizer}
                    backgroundEvents={processedBackgroundEvents}
                    toolbar={false}
                    messages={{
                        showMore(count) {
                            return `+${count} ${translate('calendar.games')}`;
                        },
                    }}
                    events={processedEvents}
                    elementProps={{ style: { height: '100%' } }}
                    startAccessor="start"
                    onRangeChange={() => {
                        // Disabled: navigation is handled by CalendarNavigation component
                    }}
                    endAccessor="end"
                    selectable={true}
                    showAllEvents={false}
                    date={currentCalendarDate}
                    onSelectEvent={(e) => {
                        onEventSelected(e);
                    }}
                    onSelectSlot={(info) => {
                        if (info.action !== 'select') return;
                        const { start, end } = info;
                        onTimeSlotSelected(start, end);
                    }}
                    views={['week', 'day']}
                    view={calendarViewType}
                    onView={(view) => setCalendarViewType(view)}
                    style={{ height: '100%' }}
                    eventPropGetter={(event, start, end, isSelected) => calendarEventPropGetter(event, start, end, isSelected)}
                />
            </Box>
        </Box>
    );
};

export default MonthlyCourtCalendar;
